import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  billingApi,
  BusinessEntitlements,
  SubscriptionPlan,
  SubscriptionStatus,
  FeatureKey,
  LimitKey,
} from '@/services/billingApi';
import { useAuth } from '@/contexts/AuthContext';
import { useBanner } from './BannerContext';

interface EntitlementsContextType {
  entitlements: BusinessEntitlements | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  syncWithStripe: () => Promise<void>;
  hasFeature: (featureKey: FeatureKey) => boolean;
  getLimit: (limitKey: LimitKey) => number;
  openUpgradeModal: (options?: {
    feature?: FeatureKey;
    title?: string;
    description?: string;
    requiredPlan?: string;
  }) => void;
  isReadOnly: boolean;
  isAllowed: boolean;
  status: SubscriptionStatus;
  plan: SubscriptionPlan | null;
  enforced: boolean;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

export const EntitlementsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { setBanner, clearBanner } = useBanner();
  const navigate = useNavigate();

  const [entitlements, setEntitlements] = useState<BusinessEntitlements | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEntitlements = useCallback(async (silent = false) => {
    if (!isAuthenticated) {
      setEntitlements(null);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await billingApi.getEntitlements();
      setEntitlements(data);
    } catch (err: any) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAuthenticated]);

  const syncWithStripe = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingApi.syncSubscription();
      setEntitlements(data);
      window.dispatchEvent(new CustomEvent('billing:refresh'));
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchEntitlements();
  }, [fetchEntitlements]);

  // Handle Stripe Checkout return redirects globally across any page
  useEffect(() => {
    if (!isAuthenticated) return;

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    const sessionId = urlParams.get('session_id');

    if (checkoutStatus === 'success' && sessionId) {
      const confirmGlobal = async () => {
        try {
          await billingApi.confirmCheckout(sessionId);
          await fetchEntitlements();
          window.dispatchEvent(new CustomEvent('billing:refresh'));
        } catch (err: any) {
          await fetchEntitlements();
          window.dispatchEvent(new CustomEvent('billing:refresh'));
        } finally {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('checkout');
          cleanUrl.searchParams.delete('session_id');
          window.history.replaceState(
            {},
            document.title,
            cleanUrl.pathname + (cleanUrl.searchParams.toString() ? `?${cleanUrl.searchParams.toString()}` : '') + cleanUrl.hash,
          );
        }
      };

      void confirmGlobal();
    }
  }, [isAuthenticated, fetchEntitlements]);

  // Handle window focus & custom billing events
  useEffect(() => {
    const handleRefresh = () => {
      void fetchEntitlements();
    };

    const handleFocus = () => {
      if (isAuthenticated) {
        void fetchEntitlements(true);
      }
    };

    window.addEventListener('billing:refresh', handleRefresh);
    window.addEventListener('billing:subscription-inactive', handleRefresh);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('billing:refresh', handleRefresh);
      window.removeEventListener('billing:subscription-inactive', handleRefresh);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchEntitlements, isAuthenticated]);

  // Synchronize global warning/info banner based on subscription status
  useEffect(() => {
    if (!entitlements || !entitlements.enforced) {
      return;
    }

    const sub = entitlements.subscription;
    const status = sub?.status;
    const isOwner = user?.role === 'OWNER';

    if (status === 'TRIALING' && sub.trialEndsAt) {
      const trialEnd = new Date(sub.trialEndsAt).getTime();
      const now = Date.now();
      const msLeft = trialEnd - now;
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

      if (daysLeft <= 5 && daysLeft > 0) {
        setBanner({
          id: 'billing-trial-expiring',
          message: `Your free trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Choose a plan to prevent interruption.`,
          variant: 'info',
          actionLabel: isOwner ? 'Choose Plan' : undefined,
          onAction: isOwner ? () => navigate('/billing?tab=plans') : undefined,
          dismissible: true,
        });
        return;
      }
    }

    if (status === 'PAST_DUE') {
      setBanner({
        id: 'billing-past-due',
        message: 'Your payment is past due. Please update your payment method to keep access active.',
        variant: 'warning',
        actionLabel: isOwner ? 'Update Billing' : undefined,
        onAction: isOwner ? () => navigate('/billing') : undefined,
        dismissible: false,
      });
      return;
    }

    if (status === 'GRACE_PERIOD') {
      const graceEndFormatted = sub.graceEndsAt
        ? new Date(sub.graceEndsAt).toLocaleDateString()
        : 'shortly';
      setBanner({
        id: 'billing-grace-period',
        message: `Payment failed. Access continues until ${graceEndFormatted}. Please update payment method to avoid lock.`,
        variant: 'warning',
        actionLabel: isOwner ? 'Fix Payment' : undefined,
        onAction: isOwner ? () => navigate('/billing') : undefined,
        dismissible: false,
      });
      return;
    }

    if (status === 'RESTRICTED' || status === 'CANCELLED') {
      setBanner({
        id: 'billing-restricted',
        message: 'Subscription is inactive. Your account is in read-only mode — changes cannot be saved.',
        variant: 'error',
        actionLabel: isOwner ? 'Reactivate' : undefined,
        onAction: isOwner ? () => navigate('/billing?tab=plans') : undefined,
        dismissible: false,
      });
      return;
    }

    // Active or no issues: clear any billing banner
    clearBanner();
  }, [entitlements, user?.role, setBanner, clearBanner, navigate]);

  const hasFeature = useCallback(
    (featureKey: FeatureKey): boolean => {
      // If billing enforcement is explicitly off
      if (entitlements && !entitlements.enforced) {
        return true;
      }
      // While loading or before entitlements exist, default basic core features to true,
      // but wait for confirmation before enabling gated/advanced features to avoid 403 race conditions
      if (loading || !entitlements) {
        const basicFeatures: FeatureKey[] = ['pos', 'inventory', 'expenses', 'basicReports', 'sync', 'onlineStore', 'onlineCheckout'];
        return basicFeatures.includes(featureKey);
      }
      return Boolean(entitlements.features[featureKey]);
    },
    [loading, entitlements],
  );

  const getLimit = useCallback(
    (limitKey: LimitKey): number => {
      if (!entitlements || !entitlements.enforced) {
        return 999999;
      }
      const val = entitlements.features[limitKey];
      return typeof val === 'number' ? val : 999999;
    },
    [entitlements],
  );

  const openUpgradeModal = useCallback(
    (options?: {
      feature?: FeatureKey;
      title?: string;
      description?: string;
      requiredPlan?: string;
    }) => {
      window.dispatchEvent(
        new CustomEvent('billing:plan-restricted', {
          detail: {
            error: options?.title || 'Plan Upgrade Required',
            details: {
              feature: options?.feature,
              requiredPlan: options?.requiredPlan ? { name: options.requiredPlan } : undefined,
            },
          },
        }),
      );
    },
    [],
  );

  const isReadOnly = Boolean(entitlements?.enforced && entitlements?.isReadOnly);
  const isAllowed = Boolean(!entitlements || !entitlements.enforced || entitlements.isAllowed);
  const status: SubscriptionStatus = entitlements?.subscription?.status || 'ACTIVE';
  const plan: SubscriptionPlan | null = entitlements?.subscription?.plan || null;
  const enforced = Boolean(entitlements?.enforced);

  return (
    <EntitlementsContext.Provider
      value={{
        entitlements,
        loading,
        error,
        refresh: fetchEntitlements,
        syncWithStripe,
        hasFeature,
        getLimit,
        openUpgradeModal,
        isReadOnly,
        isAllowed,
        status,
        plan,
        enforced,
      }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = (): EntitlementsContextType => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
};
